/**
 * Luật tích, bỏ tích của hộp Quyền đọc (ADR-0125): một cây phòng ban và người, chọn riêng là phép HỢP giữa phòng ban được
 * tích và người được chọn đích danh. Thuần, không gọi HTTP, để test bằng `node:test`. Người chưa tải do hook lo trước khi
 * gọi vào đây (`canTaiPhong`).
 *
 * `department_ids` phẳng: tích phòng cha là tích từng phòng trong nhánh, nên bỏ tích phòng con không đụng tới phòng cha.
 */
import { chuanHoaTim, nhanhCua } from '../../co-cau-to-chuc/nghiep-vu/cay.ts';
import type { NutCay } from '../../co-cau-to-chuc/nghiep-vu/cay.ts';

type PhongRutGon = { id: string; parent_id: string | null };

export interface LuaChonQuyen {
  phongBan: ReadonlySet<string>;
  /** Người được chọn đích danh, không gồm người chỉ có quyền nhờ phòng ban. */
  nguoi: ReadonlySet<string>;
}

/** Người → phòng ban trực tiếp, từ người đã tải và người trong quyền đọc hiện tại. */
export type PhongCuaNguoi = ReadonlyMap<string, string | null>;

export const khoaPhong = (id: string) => `pb:${id}`;
export const khoaNguoi = (id: string) => `ng:${id}`;

export function docKhoa(khoa: string): { loai: 'phong' | 'nguoi'; id: string } {
  return { loai: khoa.startsWith('pb:') ? 'phong' : 'nguoi', id: khoa.slice(3) };
}

/** Bỏ người đích danh có phòng ban nằm trong `nhanh`. */
function boNguoiTrongNhanh(nguoi: ReadonlySet<string>, nhanh: ReadonlySet<string>, phongCua: PhongCuaNguoi): Set<string> {
  return new Set([...nguoi].filter((u) => {
    const phong = phongCua.get(u);
    return !phong || !nhanh.has(phong);
  }));
}

/** Tích phòng ban: cả nhánh. Người đích danh trong nhánh đã có quyền nhờ phòng ban nên không giữ riêng nữa. */
export function tichPhong(ds: readonly PhongRutGon[], chon: LuaChonQuyen, id: string, phongCua: PhongCuaNguoi): LuaChonQuyen {
  const nhanh = nhanhCua(ds, id);
  return { phongBan: new Set([...chon.phongBan, ...nhanh]), nguoi: boNguoiTrongNhanh(chon.nguoi, nhanh, phongCua) };
}

/** Bỏ tích phòng ban: bỏ cả nhánh và người đích danh trong nhánh; phòng cha giữ nguyên. */
export function boTichPhong(ds: readonly PhongRutGon[], chon: LuaChonQuyen, id: string, phongCua: PhongCuaNguoi): LuaChonQuyen {
  const nhanh = nhanhCua(ds, id);
  return {
    phongBan: new Set([...chon.phongBan].filter((p) => !nhanh.has(p))),
    nguoi: boNguoiTrongNhanh(chon.nguoi, nhanh, phongCua),
  };
}

export function tichNguoi(chon: LuaChonQuyen, id: string): LuaChonQuyen {
  return { phongBan: chon.phongBan, nguoi: new Set([...chon.nguoi, id]) };
}

/** Bỏ tích người mà phòng của họ đang tích cả phòng thì phải có đủ người của phòng trước khi đổi. */
export function canTaiPhong(chon: LuaChonQuyen, phong: string | null | undefined): phong is string {
  return Boolean(phong) && chon.phongBan.has(phong!);
}

/**
 * Bỏ tích một người. Phòng của họ đang tích cả phòng thì phòng bỏ tích và những người còn lại của phòng (`nguoiTrongPhong`,
 * phải đủ) thành chọn đích danh. Phòng con là phòng riêng, giữ nguyên.
 */
export function boTichNguoi(chon: LuaChonQuyen, id: string, phong: string | null | undefined, nguoiTrongPhong: readonly string[]):
  LuaChonQuyen {
  const nguoi = new Set(chon.nguoi);
  let phongBan = chon.phongBan;
  if (canTaiPhong(chon, phong)) {
    phongBan = new Set([...chon.phongBan].filter((p) => p !== phong));
    for (const u of nguoiTrongPhong) nguoi.add(u);
  }
  nguoi.delete(id);
  return { phongBan, nguoi };
}

/** Các phòng cha của `id` (không gồm nó), dừng khi cha không có trong danh sách hoặc gặp chu trình. */
function toTien(ds: readonly PhongRutGon[], id: string): string[] {
  const theoId = new Map(ds.map((d) => [d.id, d]));
  const ra: string[] = [];
  const daQua = new Set([id]);
  for (let cha = theoId.get(id)?.parent_id; cha && theoId.has(cha) && !daQua.has(cha); cha = theoId.get(cha)?.parent_id) {
    daQua.add(cha);
    ra.push(cha);
  }
  return ra;
}

/** Phòng ban có thứ được chọn bên trong: phòng con đã tích hoặc người đích danh trong nhánh. */
function phongCoChonBenTrong(ds: readonly PhongRutGon[], chon: LuaChonQuyen, phongCua: PhongCuaNguoi): Set<string> {
  const ra = new Set<string>();
  for (const p of chon.phongBan) for (const cha of toTien(ds, p)) ra.add(cha);
  for (const u of chon.nguoi) {
    const phong = phongCua.get(u);
    if (phong) for (const x of [phong, ...toTien(ds, phong)]) ra.add(x);
  }
  return ra;
}

/** `checkedKeys` của cây: người trong phòng đã tích hiện đã tích; phòng chưa tích mà có chọn bên trong là half-check. */
export function trangThaiCay(ds: readonly PhongRutGon[], chon: LuaChonQuyen, phongCua: PhongCuaNguoi,
  nguoiDaTai: ReadonlyMap<string, ReadonlyArray<{ user_id: string }>>): { checked: string[]; halfChecked: string[] } {
  const checked = [...chon.phongBan].map(khoaPhong);
  const daCo = new Set(chon.nguoi);
  for (const p of chon.phongBan) for (const u of nguoiDaTai.get(p) ?? []) daCo.add(u.user_id);
  checked.push(...[...daCo].map(khoaNguoi));
  const halfChecked = [...phongCoChonBenTrong(ds, chon, phongCua)].filter((p) => !chon.phongBan.has(p)).map(khoaPhong);
  return { checked, halfChecked };
}

/** Nhánh mở sẵn khi vào hộp: đủ để thấy mọi thứ đang chọn; phòng có người đích danh mở luôn để thấy người đó. */
export function nhanhMoSan(ds: readonly PhongRutGon[], chon: LuaChonQuyen, phongCua: PhongCuaNguoi): string[] {
  return [...phongCoChonBenTrong(ds, chon, phongCua)].map(khoaPhong);
}

export interface NutQuyen {
  loai: 'phong' | 'nguoi';
  id: string;
  ten: string | null;
  /** Không còn gì để mở: cây không vẽ nút mở và không gọi tải người. */
  la: boolean;
  con: NutQuyen[];
}

type NguoiCay = { user_id: string; display_name: string | null };

/**
 * Dựng nút của cây từ cây phòng ban và người đã tải: phòng con trước, người sau. Có `tuKhoa` thì chỉ giữ nút khớp (không
 * phân biệt dấu) và tổ tiên của nó; nút giữ lại nào có con đều được mở (`moRong`). Tìm chỉ tới được người đã tải.
 */
export function dungNutCay(cay: readonly NutCay[], nguoiDaTai: ReadonlyMap<string, readonly NguoiCay[]>, tuKhoa: string, locale = 'vi'):
  { nut: NutQuyen[]; moRong: string[] } {
  const q = chuanHoaTim(tuKhoa);
  const khop = (ten: string | null) => !q || chuanHoaTim(ten ?? '').includes(q);
  const moRong: string[] = [];
  const dung = (ds: readonly NutCay[]): NutQuyen[] =>
    ds.flatMap((p) => {
      const nguoi = [...(nguoiDaTai.get(p.id) ?? [])]
        .sort((a, b) => (a.display_name ?? '').localeCompare(b.display_name ?? '', locale))
        .filter((u) => khop(u.display_name))
        .map((u): NutQuyen => ({ loai: 'nguoi', id: u.user_id, ten: u.display_name, la: true, con: [] }));
      const con = [...dung(p.children ?? []), ...nguoi];
      if (q && con.length === 0 && !khop(p.name)) return [];
      if (q && con.length > 0) moRong.push(khoaPhong(p.id));
      const la = q ? con.length === 0 : con.length === 0 && nguoiDaTai.has(p.id);
      return [{ loai: 'phong' as const, id: p.id, ten: p.name, la, con }];
    });
  return { nut: dung(cay), moRong };
}
